// ProjectsHubPage — UX v0.7 replacement for the old Dashboard.
//
// Two regions:
//   * Hero — "Describe what you want to build" with a Start-new-intake CTA.
//   * Project grid — cards carrying phase/gate state chips so the user
//     can see at a glance which projects need their attention.
//
// Key difference from the old Dashboard: the CTA routes to the new
// Smart Intake (/intake) — we no longer create a project up-front; the
// intake wizard creates it on submit.
//
// Plan §P2.16 — when the user has a saved Smart Intake draft, surface
// a banner above the project grid so they can pick up where they left
// off without hunting for the entry point.

import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  Folder,
  Loader2,
  Plus,
  RotateCcw,
  Sparkles,
  X,
} from 'lucide-react'

import { api } from '@/lib/api'
import type { Project, ProjectStatus } from '@/types'
import { useUxStore } from '@/stores/uxStore'
import { getShape } from '@/components/intake/shapes'
import {
  formatRelativeAge,
  useIntakeDraftStore,
} from '@/stores/intakeDraftStore'
import { SkeletonCard } from '@/components/ui/skeleton'

const STATUS_CHIP: Record<ProjectStatus, { cls: string; icon: React.ComponentType<{ className?: string }>; label: string }> = {
  intake: { cls: 'alm-chip-warn', icon: AlertCircle, label: 'intake' },
  running: { cls: 'alm-chip-info', icon: Loader2, label: 'running' },
  waiting_on_user: { cls: 'alm-chip-warn', icon: AlertCircle, label: 'needs you' },
  // Audit §2.2 — the chip vocabulary on the Hub used to read "delivered"
  // while the Command Center header rendered the raw "done". Unifying on
  // "done" so the user sees one word for one state across surfaces. The
  // canonical vocabulary lives in `frontend/src/components/ui/chip.md`.
  done: { cls: 'alm-chip-ok', icon: CheckCircle2, label: 'done' },
  archived: { cls: '', icon: Folder, label: 'archived' },
}

export function ProjectsHubPage() {
  const navigate = useNavigate()
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: api.listProjects,
  })
  const uxProjects = useUxStore((s) => s.projects)

  // Surface the saved intake draft if any meaningful work was captured.
  const draft = useIntakeDraftStore((s) => s.draft)
  const hasDraft = Boolean(
    draft && (draft.name.trim() || draft.problem_statement.trim()),
  )
  const clearDraft = useIntakeDraftStore((s) => s.clearDraft)

  return (
    <div className="min-h-[calc(100vh-96px)] text-slate-100">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Your projects</h1>
            <p className="text-slate-400 mt-1">
              Start a new spec, or resume one in progress.
            </p>
          </div>
          {/* Audit §2.1 — single primary CTA in the header. The hero
              CTA below is "Start new intake" and the header carries
              the same action without doubling as a second visually
              identical button. */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/intake/v2')}
              className="inline-flex items-center gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 px-4 py-2 text-sm font-semibold text-orange-200 hover:bg-orange-500/20"
            >
              <Sparkles className="h-4 w-4" /> New project (Carbon)
            </button>
            <button
              type="button"
              onClick={() => navigate('/intake')}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-400 hover:to-indigo-500"
            >
              <Plus className="h-4 w-4" /> New project
            </button>
          </div>
        </div>

        {/* Saved Smart Intake draft (plan §P2.16) — shown only when the
            user previously bounced out of /intake mid-flow. */}
        {hasDraft && draft ? (
          <div className="rounded-2xl border border-cyan-700/40 bg-gradient-to-b from-cyan-950/40 to-slate-900/60 p-5 mb-6 alm-fadein">
            <div className="flex items-start gap-3">
              <RotateCcw className="h-5 w-5 text-cyan-300 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-cyan-200">
                  You have a saved draft
                </div>
                <div className="text-xs text-slate-300 mt-1">
                  <span className="font-semibold text-slate-100">
                    {draft.name.trim() || 'Untitled draft'}
                  </span>
                  {' · '}
                  saved {formatRelativeAge(draft.savedAt) || 'recently'}
                  {' · '}
                  step {draft.step} of 3
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => navigate('/intake')}
                    className="inline-flex items-center gap-1.5 rounded-md bg-cyan-500 hover:bg-cyan-400 px-3 py-1.5 text-xs font-semibold text-cyan-950"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Resume intake
                  </button>
                  <button
                    type="button"
                    onClick={clearDraft}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5"
                  >
                    <X className="h-3.5 w-3.5" /> Discard
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Hero — only the dominant CTA (Start new intake). The header
            button above carries the same action; the hero focuses on
            framing what the wizard is for. */}
        <div className="rounded-2xl border border-slate-700/60 bg-slate-900/60 p-8 mb-8 relative overflow-hidden">
          <div className="absolute inset-0 alm-grid-bg opacity-60 pointer-events-none" />
          <div className="relative max-w-2xl">
            <div className="alm-chip alm-chip-info mb-3 w-fit">
              <Sparkles className="h-3 w-3" /> Smart intake · 3 steps · ~60 s
            </div>
            <h2 className="text-2xl font-semibold mb-2 text-slate-100">
              Describe what you want to build.
            </h2>
            <p className="text-slate-400 mb-5">
              We'll route your project to the right agents, ask only what matters, and produce a
              BRD/SRD, compliance notes, diagrams, wireframes, and a traceability matrix.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/intake')}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-400 hover:to-indigo-500"
              >
                Start new intake <ArrowRight className="h-4 w-4" />
              </button>
              <span className="text-xs text-slate-500">
                Average time-to-first-draft: 8 minutes
              </span>
            </div>
          </div>
        </div>

        {/* Project list */}
        {projects.isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Audit §8.5 / plan §P2.20 — replace the standalone
                centred spinner with skeleton cards that occupy the
                same shape as the loaded grid. Eliminates the layout
                jump when projects come back from the API. */}
            {[0, 1, 2].map((i) => (
              <SkeletonCard key={i} lines={4} />
            ))}
          </div>
        ) : (projects.data?.length ?? 0) === 0 ? (
          <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-12 text-center">
            <Folder className="h-10 w-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-300 font-medium">No projects yet</p>
            <p className="text-slate-500 text-sm mt-1">
              Hit <strong>New project</strong> to start your first intake.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.data?.map((project: Project) => {
              const meta = STATUS_CHIP[project.status]
              const ux = uxProjects[project.id]
              const shape = ux?.shape ? getShape(ux.shape) : null
              // Audit §2.4 — only render "run #N" when at least one run
              // has been kicked off. ``current_run`` defaults to 0 for
              // brand-new projects in the backend; we treat 0 as "not
              // yet started" so the card no longer lies about runs.
              const hasStartedRun = (project.current_run ?? 0) > 0
              return (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-5 hover:border-indigo-500/40 transition-colors flex flex-col gap-3"
                >
                  <div className="flex items-center justify-between">
                    <span className={`alm-chip ${meta.cls}`}>
                      <meta.icon
                        className={`h-3 w-3 ${project.status === 'running' ? 'animate-spin' : ''}`}
                      />{' '}
                      {meta.label}
                    </span>
                    <span className="text-xs text-slate-500 font-mono">
                      {hasStartedRun ? `run #${project.current_run}` : 'not started'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-slate-100 leading-tight">
                    {project.name}
                  </h3>
                  {project.problem_statement && (
                    <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">
                      {project.problem_statement}
                    </p>
                  )}
                  <div className="mt-auto pt-2 flex flex-wrap items-center gap-1.5 text-[10.5px]">
                    {shape && (
                      <span className="alm-chip alm-chip-info">{shape.label}</span>
                    )}
                    {project.rewind_count > 0 && (
                      <span className="alm-chip">
                        {project.rewind_count} rewind
                        {project.rewind_count === 1 ? '' : 's'}
                      </span>
                    )}
                    {project.latest_rqs != null && (
                      <span className="alm-chip alm-chip-ok">
                        <Check className="h-3 w-3" /> RQS {project.latest_rqs.toFixed(0)}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-600 font-mono truncate">
                    {project.id}
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
